export type AccessResult = "admin" | "court-reader" | "denied";

const ALLOWED_STAFF_ROLES = new Set(["owner", "admin", "staff"]);
const CATALOG_MANAGER_ROLES = new Set(["owner", "admin"]);
const CASHIER_ROLES = new Set(["owner", "admin", "staff"]);
const CASH_HISTORY_ROLES = new Set(["owner", "admin"]);
const MEMBER_MANAGER_ROLES = new Set(["owner", "admin", "staff"]);
const COURT_PRICING_MANAGER_ROLES = new Set(["owner", "admin"]);
const COURT_READER_ROLES = new Set(["owner", "admin", "staff", "coach"]);
const COURT_BOOKING_MANAGER_ROLES = new Set(["owner", "admin", "staff"]);
const EXPENSE_MANAGER_ROLES = new Set(["owner", "admin"]);
const FINANCIAL_VIEWER_ROLES = new Set(["owner", "admin"]);
const TEAM_MANAGER_ROLES = new Set(["owner", "admin"]);
// Admin só atribui/revoga abaixo dele; owner gerencia qualquer papel.
const ADMIN_ASSIGNABLE_ROLES = new Set(["staff", "coach"]);

export function resolveAccess(roleSlug: string | null): AccessResult {
  if (!roleSlug) return "denied";
  const role = roleSlug.toLowerCase();
  if (ALLOWED_STAFF_ROLES.has(role)) return "admin";
  return COURT_READER_ROLES.has(role) ? "court-reader" : "denied";
}

export function canManageCatalog(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return CATALOG_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

export function canOperateCashier(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return CASHIER_ROLES.has(roleSlug.toLowerCase());
}

export function canViewCashHistory(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return CASH_HISTORY_ROLES.has(roleSlug.toLowerCase());
}

export function canManageMembers(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return MEMBER_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

export function canManageCourtPricing(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return COURT_PRICING_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

export function canManageCourts(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return COURT_PRICING_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

export function canManageSports(roleSlug: string | null): boolean {
  return canManageCourts(roleSlug);
}

export function canManagePlans(roleSlug: string | null): boolean {
  return canManageCourts(roleSlug);
}

export function canViewBookings(roleSlug: string | null): boolean {
  return !!roleSlug && COURT_READER_ROLES.has(roleSlug.toLowerCase());
}

export function canManageCourtBookings(roleSlug: string | null): boolean {
  return !!roleSlug && COURT_BOOKING_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

export function canManageExpenses(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return EXPENSE_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

export function canViewFinancials(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return FINANCIAL_VIEWER_ROLES.has(roleSlug.toLowerCase());
}

/** Página de gestão de team (policy `TenantOwnerOrAdmin`): owner/admin. */
export function canManageTeam(roleSlug: string | null): boolean {
  if (!roleSlug) return false;
  return TEAM_MANAGER_ROLES.has(roleSlug.toLowerCase());
}

/** Se o actor pode alterar a role de um membro com a role atual informada. */
export function canEditMemberRole(actorRole: string | null, memberRoleSlug: string | null): boolean {
  const actor = actorRole?.toLowerCase();
  const member = memberRoleSlug?.toLowerCase();
  if (actor === "owner") return member !== undefined && member !== "owner";
  if (actor === "admin") return member !== undefined && ADMIN_ASSIGNABLE_ROLES.has(member);
  return false;
}

/** Se o actor pode revogar o acesso de um membro (nunca o próprio). */
export function canRevokeMember(actorRole: string | null, memberRoleSlug: string | null, isSelf: boolean): boolean {
  if (isSelf) return false;
  const actor = actorRole?.toLowerCase();
  const member = memberRoleSlug?.toLowerCase();
  if (actor === "owner") return true;
  if (actor === "admin") return member !== undefined && ADMIN_ASSIGNABLE_ROLES.has(member);
  return false;
}

/** Slugs de papel que o actor pode oferecer no select de role. */
export function assignableRoleSlugs(actorRole: string | null): string[] {
  const actor = actorRole?.toLowerCase();
  if (actor === "owner") return ["admin", "staff", "coach"];
  if (actor === "admin") return ["staff", "coach"];
  return [];
}
